import { useCallback, useState } from 'react';

import { Button } from '@/components/design-system/Button';
import { Input } from '@/components/design-system/input';
import { cn } from '@/utils/cn';
import { CloseOutlined, SearchOutlined } from '@ant-design/icons';
import { cva, type VariantProps } from 'class-variance-authority';
import { useTranslation } from 'react-i18next';

export const DEFAULT_MIN_FILTER_LENGTH = 2;

const searchInputWrapperVariants = cva('relative', {
  variants: {
    size: {
      sm: '',
      md: '',
      lg: ''
    }
  },
  defaultVariants: {
    size: 'md'
  }
});

interface SearchInputProps extends VariantProps<typeof searchInputWrapperVariants> {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  'data-testid'?: string;
}

export const SearchInput = ({
  value,
  onChange,
  placeholder,
  className,
  size = 'md',
  'data-testid': dataTestId
}: SearchInputProps) => {
  const { t } = useTranslation();
  const [isFocused, setIsFocused] = useState(false);

  // Yellow styling when: focused OR has text >= 2 characters
  const isActive = isFocused || value.length >= DEFAULT_MIN_FILTER_LENGTH;

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(e.target.value);
    },
    [onChange]
  );

  const handleClear = useCallback(() => {
    onChange('');
  }, [onChange]);

  const handleFocus = useCallback(() => {
    setIsFocused(true);
  }, []);

  const handleBlur = useCallback(() => {
    setIsFocused(false);
  }, []);

  return (
    <div className={cn(searchInputWrapperVariants({ size }), className)}>
      <SearchOutlined className="text-typography-500! pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 text-sm" />
      <Input
        placeholder={placeholder ?? t('label.search')}
        value={value}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        size={size}
        status={isActive ? 'warning' : 'default'}
        className={cn(
          'w-full pl-8',
          isActive && 'bg-warning-100 border-warning-400 hover:border-warning-600 focus-visible:border-warning-500',
          value.length > 0 && 'pr-8'
        )}
        data-testid={dataTestId}
      />
      {value.length > 0 && (
        <Button
          type="button"
          size="condensed"
          onClick={handleClear}
          className="[&_svg]:text-typography-500 hover:[&_svg]:text-typography-900 absolute top-1/2 right-3 h-auto w-auto -translate-y-1/2 cursor-pointer border-none bg-transparent p-0 text-xs hover:border-none hover:bg-transparent"
        >
          <CloseOutlined />
        </Button>
      )}
    </div>
  );
};
